import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  getFirestore, collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, query, orderBy, where, arrayUnion, serverTimestamp,
  getDocs, writeBatch,
} from 'firebase/firestore';
import { pushNotificationEvent } from '../utils/notificationRouter';
import { SessionCache } from '../cache/SessionCache';

const DB = () => { try { return getFirestore(getApp()); } catch (_) { return null; } };
const ADMIN_NAME = () => { try { return getAuth(getApp()).currentUser?.displayName || 'Admin'; } catch (_) { return 'Admin'; } };

const PRIORITY_COLOR = { info: '#65d4fa', warning: '#fbbf24', critical: '#f87171' };
const PRIORITY_BG    = { info: '#0e9cc010', warning: '#BA751710', critical: '#E24B4A10' };
const PRIORITY_ICON  = { info: 'ℹ️', warning: '⚠️', critical: '🚨' };
const PRIORITY_ORDER = { critical: 0, warning: 1, info: 2 };
const ALL_ROLES = ['admin','c_suite','payload','ops','mp','embedded','system','experimental','finance','mentor','pe','obc'];
const EXPIRE_OPTS = [
  { label: '1 hour',  ms: 60 * 60 * 1000 },
  { label: '4 hours', ms: 4 * 60 * 60 * 1000 },
  { label: '24 hours',ms: 24 * 60 * 60 * 1000 },
  { label: '7 days',  ms: 7 * 24 * 60 * 60 * 1000 },
  { label: 'Never',   ms: null },
];
const LS_KEY = 'announcements_last_read';

const S = {
  bg: '#070c1a', panel: '#0d1526', input: '#060b18',
  border: 'rgba(255,255,255,0.08)', faint: 'rgba(255,255,255,0.04)',
  text: '#d9e2ff', muted: '#bdc8ce', dim: '#64748b',
  primary: '#65d4fa', green: '#34d399', red: '#f87171', yellow: '#fbbf24',
  mono: "'Share Tech Mono','Courier New',monospace",
  sans: "'Inter',system-ui,sans-serif",
};

function fmtTs(ts) {
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function AnnouncementsPage({ userId, role }) {
  const [announcements, setAnnouncements] = useState([]);
  const [lastRead,      setLastRead]      = useState(() => parseInt(localStorage.getItem(LS_KEY) || '0', 10));
  const [createOpen,    setCreateOpen]    = useState(false);
  const [editItem,      setEditItem]      = useState(null);
  const [toast,         setToast]         = useState('');
  const isAdmin = role === 'admin';

  // Mark all as read on mount
  useEffect(() => {
    const now = Date.now();
    localStorage.setItem(LS_KEY, String(now));
    setLastRead(now);
  }, []);

  useEffect(() => {
    const db = DB();
    if (!db || !userId) return;
    const now = new Date();

    // Serve cached announcements immediately (instant back-navigation)
    SessionCache.get('announcements', userId, 'feed').then(cached => {
      if (cached?.length) {
        const visible = cached.filter(a => {
          if (a.publishAt?.toDate && a.publishAt.toDate() > now) return false;
          if (a.expiresAt?.toDate && a.expiresAt.toDate() < now) return false;
          if (a.targetRoles?.length > 0 && !a.targetRoles.includes(role) && role !== 'admin') return false;
          return true;
        });
        visible.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3));
        setAnnouncements(visible);
      }
    });

    const unsub = onSnapshot(
      query(
        collection(db, 'announcements'),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      ),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        SessionCache.set('announcements', userId, all, 'feed');
        const visible = all.filter(a => {
          if (a.publishAt?.toDate && a.publishAt.toDate() > now) return false;
          if (a.expiresAt?.toDate && a.expiresAt.toDate() < now) return false;
          if (a.targetRoles?.length > 0 && !a.targetRoles.includes(role) && role !== 'admin') return false;
          return true;
        });
        visible.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3));
        setAnnouncements(visible);
      },
      () => {}
    );
    return unsub;
  }, [role, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  async function dismiss(id) {
    const db = DB();
    if (!db) return;
    try { await updateDoc(doc(db, 'announcements', id), { readBy: arrayUnion(userId) }); } catch (_) {}
  }

  async function deactivate(id) {
    const db = DB();
    if (!db) return;
    await updateDoc(doc(db, 'announcements', id), { isActive: false });
    showToast('Deactivated');
  }

  async function remove(id) {
    if (!window.confirm('Delete this announcement permanently?')) return;
    const db = DB();
    if (!db) return;
    await deleteDoc(doc(db, 'announcements', id));
    showToast('Deleted');
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px 80px', fontFamily: S.sans }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: S.mono, fontSize: 8, color: S.primary, letterSpacing: 2, marginBottom: 4 }}>
            ANNOUNCEMENTS
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: S.text }}>Team Announcements</div>
        </div>
        {isAdmin && (
          <button
            onClick={() => setCreateOpen(true)}
            style={{
              padding: '8px 16px', fontFamily: S.mono, fontSize: 9, letterSpacing: 1,
              background: `${S.primary}15`, border: `1px solid ${S.primary}44`,
              color: S.primary, cursor: 'pointer', borderRadius: 6,
            }}
          >
            + NEW ANNOUNCEMENT
          </button>
        )}
      </div>

      {/* Feed */}
      {announcements.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          fontFamily: S.mono, fontSize: 11, color: S.dim, letterSpacing: 1,
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📣</div>
          NO ANNOUNCEMENTS
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {announcements.map(a => {
          const pc = PRIORITY_COLOR[a.priority] || S.primary;
          const bg = PRIORITY_BG[a.priority] || S.faint;
          const ts = a.createdAt?.toMillis?.() || 0;
          const isUnread = ts > lastRead;
          const isCritical = a.priority === 'critical';
          return (
            <div
              key={a.id}
              style={{
                background: bg,
                border: `1px solid ${pc}${isUnread ? '55' : '22'}`,
                borderLeft: `3px solid ${pc}`,
                borderRadius: 8,
                padding: '14px 16px',
                position: 'relative',
              }}
            >
              {isUnread && (
                <div style={{
                  position: 'absolute', top: 14, right: 14,
                  width: 8, height: 8, borderRadius: '50%', background: pc,
                }} />
              )}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>
                  {PRIORITY_ICON[a.priority] || 'ℹ️'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: S.text }}>{a.title}</span>
                    <span style={{
                      fontFamily: S.mono, fontSize: 7, color: pc,
                      background: `${pc}18`, padding: '2px 7px', letterSpacing: 1, borderRadius: 3,
                    }}>
                      {a.priority?.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: S.muted, lineHeight: 1.6, marginBottom: 8 }}>
                    {a.body}
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontFamily: S.mono, fontSize: 8, color: S.dim }}>
                    {a.createdBy && <span>Posted by {a.createdBy}</span>}
                    {a.createdAt && <span>{fmtTs(a.createdAt)}</span>}
                    {a.targetRoles?.length > 0 && (
                      <span>→ {a.targetRoles.join(', ')}</span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
                {!isCritical && (
                  <button
                    onClick={() => dismiss(a.id)}
                    style={{
                      padding: '4px 10px', fontFamily: S.mono, fontSize: 8, cursor: 'pointer',
                      background: 'transparent', border: `1px solid ${S.border}`, color: S.dim, borderRadius: 4,
                    }}
                  >DISMISS</button>
                )}
                {isAdmin && (
                  <>
                    <button
                      onClick={() => { setEditItem(a); setCreateOpen(true); }}
                      style={{
                        padding: '4px 10px', fontFamily: S.mono, fontSize: 8, cursor: 'pointer',
                        background: `${S.primary}10`, border: `1px solid ${S.primary}44`, color: S.primary, borderRadius: 4,
                      }}
                    >EDIT</button>
                    <button
                      onClick={() => deactivate(a.id)}
                      style={{
                        padding: '4px 10px', fontFamily: S.mono, fontSize: 8, cursor: 'pointer',
                        background: `${S.yellow}10`, border: `1px solid ${S.yellow}44`, color: S.yellow, borderRadius: 4,
                      }}
                    >DEACTIVATE</button>
                    <button
                      onClick={() => remove(a.id)}
                      style={{
                        padding: '4px 10px', fontFamily: S.mono, fontSize: 8, cursor: 'pointer',
                        background: `${S.red}10`, border: `1px solid ${S.red}44`, color: S.red, borderRadius: 4,
                      }}
                    >DELETE</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / Edit modal */}
      {(createOpen || editItem) && (
        <CreateModal
          existing={editItem}
          adminUid={userId}
          onClose={() => { setCreateOpen(false); setEditItem(null); }}
          onSave={showToast}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24,
          background: S.panel, border: `1px solid ${S.primary}`,
          padding: '10px 18px', fontFamily: S.mono, fontSize: 10, color: S.primary, zIndex: 9999, borderRadius: 6,
        }}>{toast}</div>
      )}
    </div>
  );
}

function CreateModal({ existing, adminUid, onClose, onSave }) {
  const [form, setForm] = useState({
    title:      existing?.title      || '',
    body:       existing?.body       || '',
    priority:   existing?.priority   || 'info',
    targetRoles: existing?.targetRoles || [],
    publishNow: true,
    scheduledAt: '',
    expireOpt:  'Never',
    sendFCM:    existing?.sendFCM ?? true,
  });
  const [busy, setBusy] = useState(false);

  const f = form;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pc = PRIORITY_COLOR[f.priority];

  function toggleRole(r) {
    set('targetRoles', f.targetRoles.includes(r)
      ? f.targetRoles.filter(x => x !== r)
      : [...f.targetRoles, r]);
  }

  async function save() {
    if (!f.title.trim() || !f.body.trim()) return;
    const db = DB();
    if (!db) return;
    setBusy(true);
    try {
      const expireMs  = EXPIRE_OPTS.find(e => e.label === f.expireOpt)?.ms;
      const publishAt = f.publishNow
        ? serverTimestamp()
        : (f.scheduledAt ? new Date(f.scheduledAt) : serverTimestamp());
      const expiresAt = expireMs ? new Date(Date.now() + expireMs) : null;

      const payload = {
        title:       f.title.trim(),
        body:        f.body.trim(),
        priority:    f.priority,
        targetRoles: f.targetRoles,
        publishAt,
        expiresAt,
        sendFCM:     f.sendFCM,
        isActive:    f.publishNow,
        readBy:      [],
        createdBy:   ADMIN_NAME(),
        createdAt:   serverTimestamp(),
      };

      if (existing) {
        await updateDoc(doc(DB(), 'announcements', existing.id), payload);
        onSave('Announcement updated!');
      } else {
        const newRef = await addDoc(collection(DB(), 'announcements'), payload);
        // Batch-write per-user notification docs so the bell rings immediately
        if (f.publishNow) {
          try {
            const usersSnap = await getDocs(collection(db, 'authorized_users'));
            const targets = usersSnap.docs
              .map(d => ({ uid: d.id, ...d.data() }))
              .filter(u => u.active !== false)
              .filter(u => f.targetRoles.length === 0 || f.targetRoles.includes(u.role));
            const batch = writeBatch(db);
            targets.forEach(u => {
              const nRef = doc(collection(db, 'notifications'));
              batch.set(nRef, {
                recipientId:    u.uid,
                type:           'ANNOUNCEMENT',
                title:          f.title.trim(),
                body:           f.body.trim(),
                priority:       f.priority,
                announcementId: newRef.id,
                navTarget:      'announcements',
                expiresAt:      expiresAt || null,
                read:           false,
                createdAt:      serverTimestamp(),
              });
            });
            await batch.commit();
          } catch (_) {}
        }
        onSave('Announcement published!');
      }
      onClose();
    } finally { setBusy(false); }
  }

  const inpStyle = {
    width: '100%', background: '#060b18', border: '1px solid rgba(255,255,255,0.08)',
    color: '#d9e2ff', padding: '8px 10px', fontFamily: "'Inter',sans-serif",
    fontSize: 12, boxSizing: 'border-box', borderRadius: 4,
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: '#00000088', zIndex: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: S.panel, border: `1px solid ${S.border}`,
        width: '100%', maxWidth: 580, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', borderRadius: 10,
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${S.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <span style={{ fontFamily: S.mono, fontSize: 9, color: S.primary, letterSpacing: 2 }}>
            {existing ? 'EDIT ANNOUNCEMENT' : 'NEW ANNOUNCEMENT'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: S.dim, cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', padding: 20, flex: 1 }}>
          <FieldLabel>TITLE</FieldLabel>
          <input value={f.title} onChange={e => set('title', e.target.value)} style={inpStyle} />
          <div style={{ marginTop: 12 }} />

          <FieldLabel>MESSAGE</FieldLabel>
          <textarea value={f.body} onChange={e => set('body', e.target.value)} rows={4}
            style={{ ...inpStyle, resize: 'vertical' }} />
          <div style={{ marginTop: 12 }} />

          <FieldLabel>PRIORITY</FieldLabel>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {['info', 'warning', 'critical'].map(p => (
              <button key={p} onClick={() => set('priority', p)}
                style={{
                  padding: '6px 14px', fontFamily: S.mono, fontSize: 9, cursor: 'pointer',
                  background: f.priority === p ? `${PRIORITY_COLOR[p]}25` : 'transparent',
                  border: `1px solid ${f.priority === p ? PRIORITY_COLOR[p] : S.border}`,
                  color: f.priority === p ? PRIORITY_COLOR[p] : S.dim, letterSpacing: 1, borderRadius: 4,
                }}>
                {p.toUpperCase()}
              </button>
            ))}
          </div>

          <FieldLabel>TARGET ROLES (empty = all)</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {ALL_ROLES.map(r => (
              <button key={r} onClick={() => toggleRole(r)}
                style={{
                  padding: '4px 10px', fontFamily: S.mono, fontSize: 8, cursor: 'pointer',
                  background: f.targetRoles.includes(r) ? `${S.primary}20` : 'transparent',
                  border: `1px solid ${f.targetRoles.includes(r) ? S.primary : S.border}`,
                  color: f.targetRoles.includes(r) ? S.primary : S.dim, borderRadius: 4,
                }}>
                {r}
              </button>
            ))}
          </div>

          <FieldLabel>AUTO-EXPIRE AFTER</FieldLabel>
          <select value={f.expireOpt} onChange={e => set('expireOpt', e.target.value)} style={{ ...inpStyle, marginBottom: 16 }}>
            {EXPIRE_OPTS.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
          </select>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <Toggle value={f.sendFCM} onChange={v => set('sendFCM', v)} />
            <span style={{ fontFamily: S.sans, fontSize: 12, color: S.muted }}>
              Send push notification to targeted roles
            </span>
          </div>

          <button onClick={save} disabled={busy}
            style={{
              width: '100%', padding: '10px', fontFamily: S.mono, fontSize: 10, letterSpacing: 1,
              background: `${pc}15`, border: `1px solid ${pc}`, color: pc,
              cursor: busy ? 'wait' : 'pointer', borderRadius: 6,
            }}>
            {busy ? 'SAVING…' : existing ? 'UPDATE ANNOUNCEMENT →' : 'PUBLISH ANNOUNCEMENT →'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{
      fontFamily: "'Share Tech Mono',monospace", fontSize: 8, color: S.dim,
      letterSpacing: 2, marginBottom: 6,
    }}>{children}</div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 36, height: 20, borderRadius: 10, cursor: 'pointer', flexShrink: 0,
        background: value ? S.primary : S.border, position: 'relative', transition: 'background 0.2s',
      }}
    >
      <div style={{
        position: 'absolute', top: 2, left: value ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
      }} />
    </div>
  );
}
